import cn from "classnames";
import type React from "react";
import { useEffect, useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import type { MatchItem, MatchedPair } from "../../../entities/Activity/types";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import style from "./MatchWordsActivityMobile.module.scss";

type Props = {
	activity: MatchItem[];
	activityId: string;
};

const shuffleArray = <T,>(array: T[]): T[] => {
	return [...array].sort(() => Math.random() - 0.5);
};

export const MatchWordsActivityMobile: React.FC<Props> = ({
	activity,
	activityId,
}) => {
	const [shuffledDefinitions, setShuffledDefinitions] = useState<string[]>([]);
	const [shuffledWords, setShuffledWords] = useState<string[]>([]);
	const [selectedDefinition, setSelectedDefinition] = useState<string | null>(
		null,
	);
	const [selectedWord, setSelectedWord] = useState<string | null>(null);
	const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
	const { triggerActivityCompletion } = useActivityCompletion();
	const [fireworksTrigger, setFireworksTrigger] = useState(false);

	useEffect(() => {
		const shuffledDefinitions = shuffleArray(
			activity.map((item) => item.definition),
		);
		const shuffledWords = shuffleArray(activity.map((item) => item.word));
		setShuffledDefinitions(shuffledDefinitions);
		setShuffledWords(shuffledWords);
	}, [activity]);

	const resetActivity = () => {
		setMatchedPairs([]);
		setSelectedDefinition(null);
		setSelectedWord(null);
		setFireworksTrigger(false);
		setShuffledDefinitions(
			shuffleArray(activity.map((item) => item.definition)),
		);
		setShuffledWords(shuffleArray(activity.map((item) => item.word)));
	};

	const checkCompletion = (newMatchedPairs: MatchedPair[]) => {
		if (newMatchedPairs.length === activity.length) {
			triggerActivityCompletion(activityId, 16, resetActivity);
			setFireworksTrigger(true);
		}
	};

	const handleDefinitionClick = (definition: string) => {
		setSelectedDefinition(definition);
		if (
			selectedWord &&
			activity.find((item) => item.definition === definition)?.word ===
				selectedWord
		) {
			const newMatchedPairs = [
				...matchedPairs,
				{ definition, word: selectedWord, matched: true },
			];
			setMatchedPairs(newMatchedPairs);
			setSelectedDefinition(null);
			setSelectedWord(null);
			checkCompletion(newMatchedPairs);
		}
	};

	const handleWordClick = (word: string) => {
		setSelectedWord(word);
		if (
			selectedDefinition &&
			activity.find((item) => item.word === word)?.definition ===
				selectedDefinition
		) {
			const newMatchedPairs = [
				...matchedPairs,
				{ definition: selectedDefinition, word, matched: true },
			];
			setMatchedPairs(newMatchedPairs);
			setSelectedDefinition(null);
			setSelectedWord(null);
			checkCompletion(newMatchedPairs);
		}
	};

	const remainingDefinitions = shuffledDefinitions.filter(
		(definition) =>
			!matchedPairs.some((pair) => pair.definition === definition),
	);

	const remainingWords = shuffledWords.filter(
		(word) => !matchedPairs.some((pair) => pair.word === word),
	);

	return (
		<div className={style.matchActivityContainer}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			<div className={style.pairsContainer}>
				<div className={style.wordsColumn}>
					{remainingWords.map((word) => (
						<div
							key={word}
							className={`${style.wordItem} ${selectedWord === word ? style.selected : ""}`}
							onClick={() => handleWordClick(word)}
							onKeyUp={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									handleWordClick(word);
								}
							}}
						>
							{word}
						</div>
					))}
				</div>
				<div className={style.definitionsColumn}>
					{remainingDefinitions.map((definition) => (
						<div
							key={definition}
							className={`${style.definitionItem} ${
								selectedDefinition === definition ? style.selected : ""
							}`}
							onClick={() => handleDefinitionClick(definition)}
							onKeyUp={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									handleDefinitionClick(definition);
								}
							}}
						>
							{definition}
						</div>
					))}
				</div>
			</div>

			<div className={style.matchedPairsContainer}>
				{matchedPairs.map((pair) => (
					<div key={pair.definition} className={cn(style.matchedItem)}>
						<div
							className={cn(style.wordItem, { [style.matched]: pair.matched })}
						>
							{pair.word}
						</div>
						<GiCheckMark />
						<div
							className={cn(style.definitionItem, {
								[style.matched]: pair.matched,
							})}
						>
							{pair.definition}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
